<template>
  <div v-if="haveBookingRequest">
    <div class="dashboard-page-title">
      <div class="container-fluid">
        <h1>Edit Request</h1>
      </div>
    </div>
    <div class="container-fluid">
      <booking-form
        :property-mutator="setRequestProperty"
        :booking="booking"
        :validation-triggered="validationTriggered"
      >
        <booking-edit-shortlist
          :booking="booking"
          slot="shortlist"/>
      </booking-form>

      <div class="dashboard-block">
        <ladda-button
          :click="handleSaveRequestCallback"
          class="btn btn-primary">
          Save Request
        </ladda-button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapState, mapGetters } from 'vuex'
import {each, omit} from 'lodash'
import { helpers } from 'mixins/bookings'
import { notify, alert } from 'utils'
import BookingForm from 'components/booking/BookingForm'
import BookingEditShortlist from 'components/booking/BookingEditShortlist'
import { OpportunityTracker } from 'mixins/analytics'

export default {
  props: ['id'],

  data () {
    return {
      validationTriggered: false,
      saveRequest: true
    }
  },

  mixins: [helpers, OpportunityTracker],

  components: {
    BookingForm,
    BookingEditShortlist
  },

  computed: {
    ...mapState('booking/request', {
      booking: 'request'
    }),
    ...mapState('booking/request', ['formStatus']),
    ...mapState('shortlist', ['shortlist']),
    ...mapGetters('booking/request', ['haveBookingRequest'])
  },

  created () {
    this.getBookingRequest({ id: this.id })
      .then(() => {
        // Prevent editing of declined bookings.
        if (this.bookingIsDeclined) {
          notify.error('Unable to edit a declined booking.')

          this.$router.push({ name: 'requests.show', params: { id: this.id } })
        }

        // Prevent editing of bookings that are no longer vetting if client.
        if (!this.bookingIsVetting && this.$userIsClient) {
          notify.error('Unable to edit a request that is no longer pending.')

          this.$router.push({ name: 'requests.show', params: { id: this.id } })
        }
      })
      .catch((error) => {
        this.handleCaughtHttpExceptionForBooking(error, 'The booking request could not be found.')
      })
  },

  methods: {
    ...mapActions('booking/request', ['getBookingRequest', 'updateBookingRequest', 'setRequestProperty', 'setFormStatus', 'getFormStatus']),
    ...mapActions('shortlist', ['clearShortlist']),

    validateAll () {
      this.validationTriggered = true
      if (this.formStatus) {
        return Promise.resolve()
      } else {
        return Promise.reject(new Error('The form has validation errors'))
      }
    },

    handleSaveRequestCallback () {
      const confirmAlertOptions = {
        'semi-critical': {
          icon: 'warning',
          dangerMode: false,
          title: this.$options.filters.sentenceCase(this.$starLabelPlural) + ' will be notified of the changes made to this request',
          text: 'Do you wish to proceed?',
          buttons: {
            confirm: {
              text: 'Confirm',
              className: 'btn-primary',
              value: 'confirm'
            }
          }
        },
        critical: {
          icon: 'warning',
          dangerMode: false,
          title: `These changes will notify ${this.$starLabel} and require them to re-apply`,
          text: 'Do you wish to proceed?',
          buttons: {
            confirm: {
              text: 'Confirm',
              className: 'btn-primary',
              value: 'confirm'
            }
          }
        }
      }

      const alertPromise = new Promise((resolve, reject) => {
        if ((!this.booking.hasOwnProperty('needsConfirmation') || !this.booking.needsConfirmation.status) || !this.$userIsAdmin) {
          resolve()
        } else {
          return alert.confirm(confirmAlertOptions[this.booking.needsConfirmation.type])
            .then((result) => {
              this.saveRequest = (result === 'confirm')
            })
            .then((resolve))
            .catch(reject)
        }
      })

      return alertPromise
        .then(() => {
          if (this.saveRequest) {
            this.validateAll().then(() => {
              const payload = omit(this.booking, ['client', 'opportunity', 'talent', 'id'])

              payload.shortlist = []
              each(this.shortlist, (star) => {
                payload.shortlist.push(star)
              })
              this.trackEvent(this.analytics.V2.JobEditEvent)
              return this.updateBookingRequest({ id: this.id, payload: payload }).then(() => {
                this.clearShortlist()
                this.setFormStatus(false)
                this.$router.push({ name: 'requests.show', params: { id: this.booking.id } })
              }).catch(() => {
                notify.error('Sorry there was an unexpected error when proccessing your request, please try again.')
              })
            }).catch(() => {
              notify.error('There were form validation issues, please fix them and try again.')
            })
          }
        })
    }
  }
}
</script>
